import Tag from "src/components/Tag/Tag";
import { formatNumber, transformRecord } from "src/utility";
import GiftcardActionCard from "../GiftcardActionCard/GiftcardActionCard";
import {CopyIcon} from "src/assets/Icons";
import { MdOutlineCalendarToday, MdVisibility, MdVisibilityOff } from "react-icons/md";
import dayjs from "dayjs";
import { STATUS_MAP } from "src/Modules/UserHistory/constants/OrdersList";
import { FC, useState } from "react";
import { useToast } from "src/context";
import { giftcardDataMap } from "../../constants";
import { useTranslation } from "react-i18next";


type GiftcardDetailsWebProps = {
  orderedAt?: string;
  status?: number;
  referenceNumber?: string;
  name?: string;
  cardPin?: string;
  brandName?: string;
  points?: number;
  value?: number;
  redemptionChannel?: string;
  imageUrl?: string;
  termsAndCondition?: string;
  usageInstructions?: string;
  cardCode?: string;
  cardExpireyDate?: string;
  provider?: string;
  isFetching: boolean;
  code?: string;
  statusEnum:keyof typeof STATUS_MAP,
};

const toObjectMap = (prev: Record<string, { display: any; label: any }>, next: { key: string; display: any; label: any }) => ({
  ...prev,
  [next.key]: { display: next.display, label: next.label },
});

const GiftcardMainDetails: FC<{props:Partial<GiftcardDetailsWebProps>}> = ({props}) => {
  const { t } = useTranslation();
  const [pinVisible, setPinVisible] = useState(false);
  const status =  STATUS_MAP?.[props.statusEnum!];
  const toast = useToast();
  const giftcardItems = transformRecord(
    {
      referenceNumber: props.referenceNumber,
      points: props.points,
      value: props.value,
      provider: props.provider,
      code: props.code,
    },
    giftcardDataMap,
  );

  const giftcardHeaderItems = transformRecord({ cardPin: props.cardPin, cardCode: props.cardCode }, giftcardDataMap, {
    disappear: !pinVisible,
  }).reduce(toObjectMap, {} as Record<string, { display: any; label: any }>);



  const handleToggleVis = () => {
    setPinVisible((v) => !v);
  };
  const handleCopy = () => {
    navigator.clipboard.writeText(giftcardHeaderItems?.cardCode?.display ?? "");
    toast.success(t("UserHistory.giftcard.copiedToClipboard"));
  };
  return (
    <div className="flex w-full flex-col gap-4 p-5 outline outline-border-default rounded-sm bg-background-default">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-0.5">
          <div className="text-font-active text-2xl font-bold">{props?.name}</div>
          <div className="text-sm text-font-default">{t("UserHistory.giftcard.redeemedForPoints", { points: formatNumber(props?.points) })}</div>
        </div>
        <div>{status && <Tag color={status.color} label={status.label} className="rounded-full px-4 py-1 text-sm outline outline-current" />}</div>
      </div>

      <div className="flex flex-col gap-3">
        <GiftcardActionCard
          variant="highlight"
          label={t("UserHistory.giftcard.voucherCode")}
          value={giftcardHeaderItems?.cardCode?.display}
          action={
            <button
              type="button"
              onClick={handleCopy}
              className="flex cursor-pointer items-center gap-2 rounded-md bg-background-primary px-4 py-2 text-sm font-medium text-font-inverted transition-colors hover:bg-background-primary-dark"
            >
              <CopyIcon className="size-4" />
              <span className="hidden sm:inline">{t("UserHistory.giftcard.copyCode")}</span>
            </button>
          }
        />
        <div className="flex flex-col gap-3 md:flex-row">
          <GiftcardActionCard
            className="flex-1"
            label={t("UserHistory.giftcard.securityPin")}
            value={giftcardHeaderItems?.cardPin?.display}
            action={
              <button
                type="button"
                onClick={handleToggleVis}
                className="flex cursor-pointer items-center gap-1 text-sm font-medium text-font-active hover:text-primary-dark"
              >
                {pinVisible ? <MdVisibilityOff className="size-4" /> : <MdVisibility className="size-4" />}
                {pinVisible ? t("UserHistory.giftcard.hide") : t("UserHistory.giftcard.show")}
              </button>
            }
          />
          {props.cardExpireyDate && (
            <GiftcardActionCard
              className="flex-1"
              leadingIcon={<MdOutlineCalendarToday className="size-5" />}
              label={t("UserHistory.giftcard.validUntil")}
              value={dayjs(props.cardExpireyDate).format("DD MMMM YYYY")}
            />
          )}
        </div>
      </div>
      <div className="border-t border-dashed border-border-default pt-4">
        <div className="grid md:grid-cols-3 gap-x-4 gap-y-5 grid-cols-2">
          {giftcardItems.map(({ key, label, display }) => (
            display && <div key={key} className="flex flex-col gap-0.5">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-font-subtle">{label}</div>
              <div className="text-sm font-semibold text-font-default">{display}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GiftcardMainDetails;
